import { z } from 'zod'

import {
  AgentEvidenceStatusEnum,
  LaneRationaleCitationSchema,
  LaneRationaleSchema,
  ProposalEvidenceSchema
} from './agent/evidence.ts'
import { idSchema, msTimestampSchema, MutationIdSchema, PrivacyLevelEnum } from './common.ts'
import { CreatorInfoProviderCodeEnum } from './creator-info.ts'
import { DeliveryHistoryEntrySchema, DeliverySchema, ProposalDeliveryStateEnum } from './delivery.ts'

// 'snoozed' (RAL-108) parks the proposal with a wake time; it's a live, non-terminal status like
// 'held', not a rejection — see Proposal.isLive() below and backend/routes/api/proposals.ts's
// snooze/wake routes.
// 'cancelled' (RAL-113) is what an approved-and-scheduled proposal becomes when the creator
// pauses before it actually publishes — terminal, distinct from 'rejected_by_user' (a human
// judgment call) and 'superseded' (replaced by a rewrite). See backend/routes/api/campaign.ts's
// pause route and publisher.ts's executeDelivery skip check.
// 'slot_missed' (RAL-115/RAL-116) is the same cancellation mechanics but Rally-triggered — the
// pause fired automatically because the TikTok connection died, not because the creator acted.
// RAL-100's publishing ledger renders these as two different row kinds ("nobody · never
// offered" vs "You") and neither counts toward the autonomy track record the way a real
// publish_failed does. See services/campaign/pause.ts's `actor` param.
export const ProposalStatusEnum = z.enum([
  'auto_approved',
  'held',
  'approved_by_user',
  'rejected_by_user',
  'superseded',
  'superseded_by_retry',
  'snoozed',
  'cancelled',
  'slot_missed',
  'system_error'
])
export type ProposalStatus = z.infer<typeof ProposalStatusEnum>

export const ProposalSystemErrorCodeEnum = z.enum([
  'model_retired',
  'model_unavailable',
  'model_throttled',
  'invalid_model_output',
  'llm_request_failed'
])
export type ProposalSystemErrorCode = z.infer<typeof ProposalSystemErrorCodeEnum>

const ProposalSystemErrorSampleSchema = z.object({
  code: ProposalSystemErrorCodeEnum,
  message: z.string().min(1).max(500)
})

export const ProposalSystemErrorSchema = z.object({
  code: ProposalSystemErrorCodeEnum,
  message: z.string().min(1).max(500),
  modelId: z.string().min(1).max(200).nullable(),
  occurredAt: msTimestampSchema,
  sampleErrors: z.array(ProposalSystemErrorSampleSchema).max(3)
})
export type ProposalSystemError = z.infer<typeof ProposalSystemErrorSchema>

export const RejectReasonEnum = z.enum([
  'weak_hook',
  'wrong_audio',
  'already_posted_similar',
  'rights_unclear',
  'off_brand'
])
export type RejectReason = z.infer<typeof RejectReasonEnum>

export const ProposalAssetResponseSchema = z.object({
  id: idSchema,
  originalFilename: z.string().min(1),
  storageKey: z.string().min(1),
  previewUrl: z.string().url(),
  // Signed like the library's, and null when the asset has no thumbnail yet rather than falling
  // back to a placeholder URL the client would have to special-case anyway (RAL-202).
  thumbnailUrl: z.string().url().nullable().default(null),
  durationSeconds: z.number().nullable(),
  sidecarNote: z.string().nullable(),
  addedAt: msTimestampSchema
})

// RAL-219: one lane per target channel. Lanes share the proposal's asset, approval and base time
// (`publishAt`); each carries its own copy and an offset from that base time. Approval turns each
// lane into a delivery. Capped at 48h to match the slot window the agent paces lanes into.
export const MAX_LANE_OFFSET_MINUTES = 48 * 60
export const ProposalLaneSchema = z.object({
  channelId: idSchema,
  caption: z.string().min(1),
  hashtags: z.array(z.string()).default([]),
  title: z.string().nullable().default(null),
  offsetMinutes: z.number().int().nonnegative().max(MAX_LANE_OFFSET_MINUTES),
  // RAL-239: set only by the agent, only on a lane that differs from the base, and only with a
  // citation from that lane's channel. A lane edit that changes the copy or offset clears both.
  rationale: LaneRationaleSchema.nullable().default(null),
  rationaleCitation: LaneRationaleCitationSchema.nullable().default(null)
})
export type ProposalLane = z.infer<typeof ProposalLaneSchema>
export type ProposalLaneInput = z.input<typeof ProposalLaneSchema>

// What per-lane pacing did to the agent's lanes (RAL-225), recorded on the proposal (RAL-228).
export const LaneDropReasonEnum = z.enum([
  'channel_not_offered',
  'duplicate_channel',
  'invalid_lane',
  'no_open_slot',
  // A campaign channel the chosen clip was never offered to because it fails that channel's media
  // limits (duration, aspect ratio). Recorded so the Queue can say why the lane is missing.
  'media_limits'
])
export type LaneDropReason = z.infer<typeof LaneDropReasonEnum>

export const LaneMoveReasonEnum = z.enum(['blackout', 'daily_cap', 'min_interval'])
export type LaneMoveReason = z.infer<typeof LaneMoveReasonEnum>

export const DroppedLaneSchema = z.object({ channelId: z.string(), reason: LaneDropReasonEnum })
export type DroppedLane = z.infer<typeof DroppedLaneSchema>

export const MovedLaneSchema = z.object({
  channelId: z.string(),
  reason: LaneMoveReasonEnum,
  fromOffsetMinutes: z.number(),
  toOffsetMinutes: z.number()
})
export type MovedLane = z.infer<typeof MovedLaneSchema>

export const ProposalLanesUpdateBodySchema = z.object({
  lanes: z
    .array(ProposalLaneSchema)
    .min(1)
    .refine((lanes) => new Set(lanes.map((lane) => lane.channelId)).size === lanes.length, {
      message: 'each channel may appear in at most one lane'
    })
})
export type ProposalLanesUpdateBody = z.infer<typeof ProposalLanesUpdateBodySchema>

// RAL-237: cancels an approved proposal's scheduled lanes. Omitting `channelIds` means every lane.
export const ProposalCancelBodySchema = z.object({ channelIds: z.array(idSchema).min(1).optional() }).strict()
export type ProposalCancelBody = z.infer<typeof ProposalCancelBodySchema>

// Every targeted lane as it stands after the cancel: `cancelled` if it was still scheduled, otherwise
// unchanged, so the Queue can say why a lane that was already publishing or published kept going.
export const ProposalCancelResponseSchema = z.object({
  proposalStatus: ProposalStatusEnum,
  deliveries: z.array(DeliverySchema)
})
export type ProposalCancelResponse = z.infer<typeof ProposalCancelResponseSchema>

export const DeliverySettingSchema = z.object({
  channelId: idSchema,
  privacyLevel: PrivacyLevelEnum,
  musicUsageConfirmed: z.literal(true)
})
export type DeliverySetting = z.infer<typeof DeliverySettingSchema>

export const ApproveProposalBodySchema = z
  .object({
    caption: z.string().min(1).optional(),
    hashtags: z.array(z.string()).optional(),
    publishAt: msTimestampSchema.optional(),
    mutationId: MutationIdSchema.optional(),
    deliverySettings: z
      .array(DeliverySettingSchema)
      .min(1)
      .refine((settings) => new Set(settings.map((setting) => setting.channelId)).size === settings.length, {
        message: 'each channel may appear at most once'
      })
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.caption !== undefined && body.mutationId === undefined) {
      ctx.addIssue({ code: 'custom', path: ['mutationId'], message: 'mutationId is required when caption changes' })
    }
    if (body.caption === undefined && body.mutationId !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['mutationId'], message: 'mutationId requires a caption change' })
    }
  })
export type ApproveProposalBody = z.infer<typeof ApproveProposalBodySchema>

export const PublishSettingsConflictCodeEnum = z.enum([
  'delivery_settings_mismatch',
  'creator_info_unavailable',
  'privacy_level_option_mismatch',
  'video_duration_exceeded'
])
export type PublishSettingsConflictCode = z.infer<typeof PublishSettingsConflictCodeEnum>

export const PublishSettingsConflictResponseSchema = z.object({
  error: z.object({
    code: z.union([PublishSettingsConflictCodeEnum, CreatorInfoProviderCodeEnum]),
    message: z.string().min(1),
    channelId: idSchema.optional(),
    privacyLevelOptions: z.array(PrivacyLevelEnum).optional()
  })
})
export type PublishSettingsConflictResponse = z.infer<typeof PublishSettingsConflictResponseSchema>

// Why a held proposal is sitting in the queue. Ordered by precedence, not severity: a locked
// ramp-up gate decides the outcome on its own, so a threshold comparison below it is noise —
// showing "below threshold" when the gate is what held the proposal tells the creator the wrong
// thing to fix (RAL-200).
// Which end of a status bucket GET /api/proposals reads first. Oldest is the default the held queue
// has always used; decided buckets only grow, so the Queue's Approved tab reads the newest.
export const ProposalListOrderEnum = z.enum(['oldest', 'newest'])
export type ProposalListOrder = z.infer<typeof ProposalListOrderEnum>

export const ProposalHoldReasonEnum = z.enum(['ramp_up_locked', 'below_threshold', 'awaiting_decision'])
export type ProposalHoldReason = z.infer<typeof ProposalHoldReasonEnum>

// RAL-240: one pending lane's daily-cap gate. `committed` is what pacing counts on the lane channel for
// the lane's local day (campaign timezone): its scheduled, publishing and published deliveries, before
// this proposal's own lanes exist. Both counts are null when the lane's channel is gone, so the gates
// stay aligned with the lanes.
export const LaneGateSchema = z.object({
  channelId: idSchema,
  committed: z.number().int().nonnegative().nullable(),
  maxPostsPerDay: z.number().int().positive().nullable()
})
export type LaneGate = z.infer<typeof LaneGateSchema>

// API responses intentionally omit account-scoped internals while adding the signed preview asset.
export const ProposalListItemResponseSchema = z.object({
  id: idSchema,
  assetId: idSchema,
  status: ProposalStatusEnum,
  caption: z.string().min(1),
  hashtags: z.array(z.string()),
  publishAt: msTimestampSchema,
  confidence: z.number().min(0).max(1),
  rawConfidence: z.number().min(0).max(1),
  reasoning: z.string(),
  agentVersion: z.string().min(1),
  createdAt: msTimestampSchema,
  decidedAt: msTimestampSchema.nullable(),
  rejectReason: RejectReasonEnum.nullable(),
  rejectNote: z.string().nullable(),
  systemError: ProposalSystemErrorSchema.nullable().default(null),
  snoozedUntil: msTimestampSchema.nullable(),
  lanes: z.array(ProposalLaneSchema).default([]),
  movedLanes: z.array(MovedLaneSchema).default([]),
  droppedLanes: z.array(DroppedLaneSchema).default([]),
  assetHistory: DeliveryHistoryEntrySchema.nullable().default(null),
  // Empty and null until the proposal is decided; the state is derived from `deliveries`.
  deliveries: z.array(DeliverySchema).default([]),
  deliveryState: ProposalDeliveryStateEnum.nullable().default(null),
  // One per lane in lane order, on the Queue list's undecided proposals only.
  laneGates: z.array(LaneGateSchema).default([]),
  asset: ProposalAssetResponseSchema
})
// The fields a reader needs from one sampled decision. `samples` stays `z.array(z.unknown())` on
// the response because it is an audit dump written by the agent, but anything *rendered* from it
// gets parsed through this first rather than being indexed into as `any`. Loose, because a sample
// carries more than this and older rows may carry less (RAL-203).
export const ProposalSampleSummarySchema = z
  .object({
    decision: z.enum(['publish', 'hold_all']).catch('hold_all'),
    chosen_asset_id: z.string().nullable().catch(null),
    confidence: z.number().min(0).max(1).nullable().catch(null)
  })
  .loose()
export type ProposalSampleSummary = z.infer<typeof ProposalSampleSummarySchema>

export const ProposalDetailResponseSchema = ProposalListItemResponseSchema.extend({ samples: z.array(z.unknown()) })
export const ProposalsListResponseSchema = z.object({ proposals: z.array(ProposalListItemResponseSchema) })
export type ProposalListItemResponse = z.infer<typeof ProposalListItemResponseSchema>
export type ProposalDetailResponse = z.infer<typeof ProposalDetailResponseSchema>
export type ProposalsListResponse = z.infer<typeof ProposalsListResponseSchema>
export type ProposalListItemResponseInput = z.input<typeof ProposalListItemResponseSchema>
export type ProposalDetailResponseInput = z.input<typeof ProposalDetailResponseSchema>
export type ProposalsListResponseInput = z.input<typeof ProposalsListResponseSchema>

export const ProposalSchema = z.object({
  id: idSchema,
  // Proposal rows are keyed by (assetId, id) — not accountId — so a bare row (e.g. the payload
  // handed to Proposal.addEventListeners()'s data.on('created:proposal:*', ...) handler) can't
  // resolve which account owns it without this. Required, not defaulted: every real proposal is
  // created from backend/services/agent/agent.ts's runTick(accountId, ...), which always has it.
  accountId: idSchema,
  workspaceId: idSchema,
  campaignId: idSchema,
  assetId: idSchema,
  caption: z.string().min(1),
  creatorGuidance: z.string().nullable().default(null),
  hashtags: z.array(z.string()).default([]),
  publishAt: msTimestampSchema,
  rawConfidence: z.number().min(0).max(1),
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  samples: z.array(z.unknown()).default([]),
  agentVersion: z.string().min(1),
  status: ProposalStatusEnum.default('held'),
  createdAt: msTimestampSchema,
  decidedAt: msTimestampSchema.nullable().default(null),
  // RAL-108's reject-reason contract: recorded on this row (the closest thing this codebase has
  // today to RAL-58's not-yet-built immutable decision-audit table) alongside the existing
  // decidedAt/confidence/status fields already on this record.
  rejectReason: RejectReasonEnum.nullable().default(null),
  // User/support-facing only — never read by the model (RAL-97 rule #7).
  rejectNote: z.string().nullable().default(null),
  evidence: ProposalEvidenceSchema.default(() => ({
    status: AgentEvidenceStatusEnum.enum.insufficient_data,
    citations: [],
    exploration: null
  })),
  systemError: ProposalSystemErrorSchema.nullable().default(null),
  retryOfProposalId: idSchema.nullable().default(null),
  // Set when status is 'snoozed'; the scheduled wake time. Null means "rest of campaign" —
  // parked indefinitely, woken only by an explicit "Wake now" (no campaign end-date field exists
  // yet to compute a real date against).
  snoozedUntil: msTimestampSchema.nullable().default(null),
  // Editable while the proposal is undecided; approval writes one delivery per lane.
  lanes: z.array(ProposalLaneSchema).default([]),
  // What per-lane pacing did when the agent proposed (RAL-225), kept on the row so the Queue can name
  // a moved or dropped lane without reading the agent decision. Not updated by lane edits.
  movedLanes: z.array(MovedLaneSchema).default([]),
  droppedLanes: z.array(DroppedLaneSchema).default([]),
  // The clip's most recent published lane when the agent proposed it, views as read then.
  assetHistory: DeliveryHistoryEntrySchema.nullable().default(null)
})

export type ProposalRow = z.infer<typeof ProposalSchema>

// A proposal that still represents a live claim on the asset: undecided, or parked. Used by
// backend/routes/api/assets.ts to pick the delete tier (a snoozed proposal is still "live" —
// it will wake and reclaim the clip — even though nothing is currently scheduled to publish).
export function isLiveProposalStatus(status: ProposalStatus): boolean {
  return status === 'held' || status === 'auto_approved' || status === 'approved_by_user' || status === 'snoozed'
}
